/**
 * PostHog (Haddock / Haddock HR): identify + eventos custom.
 * Requiere que PostHog esté cargado en window (snippet o SDK).
 *
 * Solo producción real: un `npm start` en hr.haddock.app (hosts/SSO) con DB
 * anonimizada no debe escribir en el proyecto PostHog de Haddock.
 * `REACT_APP_ENV=haddock` es el perfil local, no producción.
 * st-hr.haddock.app apunta al pre: ni el snippet ni estos eventos deben correr ahí.
 */

const HADDOCK_PRODUCTION_HOST = "hr.haddock.app";

export const isHaddockTrackingContext = (
	runtime = {
		nodeEnv: process.env.NODE_ENV,
		appEnv: process.env.REACT_APP_ENV,
		hostname:
			typeof window !== "undefined" ? window.location.hostname : "",
	}
) =>
	runtime.nodeEnv === "production" &&
	runtime.appEnv === "production" &&
	runtime.hostname === HADDOCK_PRODUCTION_HOST;

const getRoutePath = () => {
	if (typeof window === "undefined") return "";
	return window.location?.pathname || "";
};

const getRolesArray = (user) => {
	if (!user) return [];
	if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
	if (user.role) return [user.role];
	return [];
};

const getCompanyFromEntity = (entity) => {
	if (!entity) return null;
	if (entity.company) return entity.company;
	if (entity.employee_with_contract?.company) return entity.employee_with_contract.company;
	return null;
};

/**
 * ID interno Plain del venue activo. Prioriza user.company.id para mantener
 * company_id y property_id alineados (evita user.company_id obsoleto tras cambio de venue).
 */
const getCompanyIdFromEntity = (entity) => {
	const company = getCompanyFromEntity(entity);
	if (company?.id != null) return company.id;
	return entity?.company_id ?? null;
};

/** ID de venue en Haddock (property_id). En Plain se persiste como company.code. */
const getPropertyIdFromEntity = (entity) => getCompanyFromEntity(entity)?.code ?? null;

/** Props de venue alineadas; fallback para fichaje terminal (employee + user). */
export const resolveHaddockVenueProps = (primary, fallback) => ({
	company_id: getCompanyIdFromEntity(primary) ?? getCompanyIdFromEntity(fallback) ?? null,
	property_id: getPropertyIdFromEntity(primary) ?? getPropertyIdFromEntity(fallback) ?? null,
});

const getRole = (user) => {
	if (!user) return "unknown";
	const roles = getRolesArray(user);
	if (roles.length > 0) return roles[0];
	return "unknown";
};

const isAdminUser = (user) => getRolesArray(user).includes("ADMIN");

/**
 * Propiedades de persona alineadas con el snippet de referencia + datos para segmentación en PostHog (surveys, cohorts).
 * Sin app_env (petición explícita del equipo).
 */
export const buildPosthogIdentifyProperties = (user) => {
	const roles = getRolesArray(user);
	const company = getCompanyFromEntity(user);
	const language =
		typeof navigator !== "undefined" && navigator.language
			? navigator.language
			: "";

	return {
		userId: user.id,
		email: user.email ?? null,
		name: user.name ?? null,
		phone: user.phone ?? "",
		language,
		roles,
		roles_joined: roles.join(","),
		is_admin: roles.includes("ADMIN"),
		is_supervisor: roles.includes("SUPERVISOR"),
		company_id: getCompanyIdFromEntity(user),
		company_name: company?.name ?? null,
		company_created_at: company?.created ?? company?.created_at ?? null,
		property_id: getPropertyIdFromEntity(user),
	};
};

/** Firma para evitar identify redundante; incluye venue para re-identificar al cambiar de company. */
export const buildPosthogIdentifySignature = (user) => {
	if (!user?.id) return "";
	const roles = getRolesArray(user);
	const company = getCompanyFromEntity(user);
	return [
		user.id,
		roles.join(","),
		getCompanyIdFromEntity(user) ?? "",
		company?.name ?? "",
		company?.created ?? company?.created_at ?? "",
		getPropertyIdFromEntity(user) ?? "",
	].join("|");
};

export const identifyHaddockUser = (user) => {
	if (!isHaddockTrackingContext()) return false;
	if (!user?.id) return false;
	if (typeof window === "undefined" || !window.posthog?.identify) return false;

	window.posthog.identify(String(user.id), buildPosthogIdentifyProperties(user));
	return true;
};

export const trackHaddockEvent = (eventName, properties = {}) => {
	if (!isHaddockTrackingContext()) return false;
	if (typeof window === "undefined" || !window.posthog?.capture) return false;

	window.posthog.capture(eventName, {
		route_path: getRoutePath(),
		timestamp_client: new Date().toISOString(),
		...properties,
	});

	return true;
};

/** Props comunes de actor + venue en eventos custom. */
export const getHaddockActorProps = (user) => ({
	actor_user_id: user?.id || null,
	actor_role: getRole(user),
	company_id: getCompanyIdFromEntity(user),
	property_id: getPropertyIdFromEntity(user),
});

/** @deprecated Usar getHaddockActorProps */
export const getApprovalActorProps = getHaddockActorProps;

/** Login de administrador (MAU/WAU). Una vez por sesión de usuario en App.js. */
export const trackHaddockAdminLogin = (user) => {
	if (!isAdminUser(user)) return false;

	return trackHaddockEvent("auth__admin_session__started__app", {
		...getHaddockActorProps(user),
	});
};

/** Publicar planificación: fecha de visibilidad (published_until) guardada. */
export const trackHaddockPlanningPublished = (user, { group_id, published_until } = {}) =>
	trackHaddockEvent("planning__group_published__saved__plan_page", {
		...getHaddockActorProps(user),
		group_id: group_id ?? null,
		published_until: published_until ?? null,
	});

/** Planificación creada vía planificación inteligente. */
export const trackHaddockPlanningIntelligentSaved = (
	user,
	{ group_id, shift_count, start, end } = {}
) =>
	trackHaddockEvent("planning__intelligent_plan__saved__plan_magic_page", {
		...getHaddockActorProps(user),
		group_id: group_id ?? null,
		shift_count: shift_count ?? null,
		plan_start: start ?? null,
		plan_end: end ?? null,
	});

/** Asignación de turno creada en la rejilla de planificación. */
export const trackHaddockPlanningShiftCreated = (user, { group_id, day } = {}) =>
	trackHaddockEvent("planning__shift_assignment__created__plan_page", {
		...getHaddockActorProps(user),
		group_id: group_id ?? null,
		plan_day: day ?? null,
	});

/** Subida de documento(s) por administrador. */
export const trackHaddockDocumentUpload = (
	user,
	{ document_count, upload_mode, employee_id, source } = {}
) => {
	if (!isAdminUser(user)) return false;

	return trackHaddockEvent("document__upload__completed__documents_page", {
		...getHaddockActorProps(user),
		document_count: document_count ?? null,
		upload_mode: upload_mode ?? null,
		target_employee_id: employee_id ?? null,
		upload_source: source ?? "documents_page",
	});
};

/** Fichaje de empleado (DAU por venue). */
export const trackHaddockEmployeeClockIn = (
	user,
	{ employee_id, clock_action, source, company_id, property_id } = {}
) => {
	const actorProps = user ? getHaddockActorProps(user) : {};
	const resolvedSource = source ?? "dashboard_widget";
	const eventName =
		resolvedSource === "web_terminal"
			? "time_clock__record__created__web_terminal"
			: "time_clock__record__created__dashboard_widget";

	return trackHaddockEvent(eventName, {
		...actorProps,
		employee_id: employee_id ?? user?.id ?? null,
		clock_action: clock_action ?? "entrada",
		source: resolvedSource,
		company_id: company_id ?? actorProps.company_id ?? null,
		property_id: property_id ?? actorProps.property_id ?? null,
	});
};
